/**
 * The client app runs both inside the Telegram mini-app and as a plain website
 * (02-architecture §8). Everything Telegram-specific goes through these helpers,
 * so tests can fake `window.Telegram.WebApp`.
 */
import { openNativeUrl } from './nativeBridge'

export interface TelegramBackButton {
  show(): void
  hide(): void
  onClick(handler: () => void): void
  offClick(handler: () => void): void
}

export interface TelegramWebApp {
  initData: string
  initDataUnsafe?: { user?: { language_code?: string } }
  colorScheme?: 'light' | 'dark'
  BackButton?: TelegramBackButton
  openLink?(url: string): void
  /** Попросить у пользователя его номер: Telegram отправит контакт боту (Bot API 6.9). */
  requestContact?(callback?: (shared: boolean) => void): void
  /**
   * Отключить сворачивание мини-аппа свайпом вниз (Bot API 7.7). Иначе этот
   * жест Telegram срабатывает вместе со смахиванием нашей шторки.
   */
  disableVerticalSwipes?(): void
  ready(): void
  expand(): void
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null
}

/** True only when Telegram actually handed us initData to authenticate with. */
export function isTelegramMiniApp(): boolean {
  const webApp = getTelegramWebApp()
  return Boolean(webApp?.initData)
}

export function telegramLanguage(): string | undefined {
  return getTelegramWebApp()?.initDataUnsafe?.user?.language_code
}

/**
 * Внешняя ссылка из мини-аппа уходит через Telegram: обычный переход увёл бы
 * WebView с приложения. Вернёт false на сайте — там работает сама ссылка.
 */
export function openExternalLink(url: string): boolean {
  const webApp = getTelegramWebApp()
  if (!webApp?.initData || !webApp.openLink) return false
  webApp.openLink(url)
  return true
}

/**
 * Открыть чужую страницу, не теряя приложение: в нашем Flutter-приложении —
 * через его мост, в Telegram — его браузером, на сайте — новой вкладкой. Так
 * страница оплаты не заменяет приложение, а покупатель, заплатив, возвращается
 * туда, где ждёт его заказ.
 */
export function openOutside(url: string): void {
  if (openNativeUrl(url) || openExternalLink(url)) return
  window.open(url, '_blank', 'noopener')
}

/** Подписанная строка запуска мини-аппа; пусто — открыто не в Telegram. */
export function telegramInitData(): string {
  return getTelegramWebApp()?.initData ?? ''
}

/**
 * Мини-апп готов: Telegram убирает заставку и разворачивает окно на весь рост.
 * Свайп вниз оставляем приложению: шторки закрываются им сами, а свернуть
 * мини-апп можно кнопкой Telegram.
 */
export function startTelegram(): void {
  const webApp = getTelegramWebApp()
  if (!webApp) return
  webApp.ready()
  webApp.expand()
  webApp.disableVerticalSwipes?.()
}

/** Telegram умеет попросить номер у пользователя — в старых клиентах нет. */
export function canShareContact(): boolean {
  return isTelegramMiniApp() && typeof getTelegramWebApp()?.requestContact === 'function'
}

/**
 * Номер из Telegram: пользователь подтверждает, Telegram отправляет его
 * контакт нашему боту, а бот привязывает номер на сервере. Сам номер сюда не
 * приходит — только «поделился» или нет.
 */
export function shareTelegramContact(): Promise<boolean> {
  return new Promise((resolve) => {
    const webApp = getTelegramWebApp()
    if (!webApp?.requestContact) {
      resolve(false)
      return
    }
    webApp.requestContact((shared) => {
      resolve(shared)
    })
  })
}
